import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { APP_VERSION } from '@/utils/version';

type ReleaseRow = {
  version: string;
  build_number?: number | null;
  notes?: string | null;
  min_supported_version?: string | null;
  force_update?: boolean | null;
  platform?: string | null;
  created_at?: string | null;
};

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10));
  const pb = b.split('.').map((x) => parseInt(x, 10));
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }

  return 0;
}

const UpdatesCard: React.FC = () => {
  const { toast } = useToast();
  const [latest, setLatest] = useState<ReleaseRow | null>(null);
  const [checking, setChecking] = useState(false);
  const [forceModalOpen, setForceModalOpen] = useState(false);

  const forceUpdateRequired = useMemo(() => {
    if (!latest) return false;
    if (latest.force_update) return true;
    if (latest.min_supported_version && compareVersions(APP_VERSION, latest.min_supported_version) < 0) {
      return true;
    }
    return false;
  }, [latest]);

  const statusText = useMemo(() => {
    if (!latest) return 'Unknown';
    if (forceUpdateRequired) return 'Update required';
    if (compareVersions(latest.version, APP_VERSION) > 0) return 'Update available';
    return 'Up to date';
  }, [forceUpdateRequired, latest]);

  const fetchLatestRelease = useCallback(async () => {
    const { data, error } = await supabase
      .from('app_releases')
      .select('version, notes, min_supported_version, force_update, platform, created_at')
      .eq('platform', 'web')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Failed to fetch latest release:', error);
      return null;
    }

    const row = data?.[0] ?? null;
    setLatest(row);
    return row;
  }, []);

  const checkForUpdates = useCallback(async () => {
    setChecking(true);
    const release = await fetchLatestRelease();

    toast({
      title: 'Release check complete',
      description: release
        ? `Installed: ${APP_VERSION} • Latest: ${release.version} • Status: ${compareVersions(release.version, APP_VERSION) > 0 ? 'Update available' : 'Up to date'}`
        : `Installed: ${APP_VERSION} • Status: ${statusText}`,
    });

    if (forceUpdateRequired) {
      setForceModalOpen(true);
    }

    setChecking(false);
  }, [fetchLatestRelease, forceUpdateRequired, statusText, toast]);

  const reloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    fetchLatestRelease();
  }, [fetchLatestRelease]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>About & Updates</CardTitle>
        <CardDescription>Review the latest deployed version of the SSR website and reload when a new release is live.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-muted-foreground">Installed version</Label>
              <p className="font-medium">{APP_VERSION}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Latest version</Label>
              <p className="font-medium">{latest?.version ?? '—'}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Status</Label>
              <p className="font-medium">{statusText}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Platform</Label>
              <p className="font-medium">Web (SSR)</p>
            </div>
          </div>

          {latest?.notes && (
            <div className="rounded-md border bg-muted/30 p-3">
              <p className="whitespace-pre-line text-sm text-muted-foreground">{latest.notes}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={checkForUpdates} disabled={checking}>
              {checking ? 'Checking...' : 'Check for updates'}
            </Button>
            <Button variant="secondary" onClick={reloadApp} disabled={checking}>
              Reload page
            </Button>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={forceUpdateRequired && forceModalOpen} onOpenChange={setForceModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Critical update required</AlertDialogTitle>
            <AlertDialogDescription>
              Your deployed site version ({APP_VERSION}) is below the minimum supported version
              {latest?.min_supported_version ? ` (${latest.min_supported_version})` : ''}. Please reload to fetch the current SSR release.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Later</AlertDialogCancel>
            <AlertDialogAction onClick={reloadApp}>Reload now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default UpdatesCard;
